import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { fetchAutomationRules, updateRule } from '../../pages/settings/api/automation-rules';
import { ACTION_TYPE_SET_SERVICE_COLLECTION } from '../../pages/settings/automation-rules/constants/action-types';
import { NotificationService, NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { LogService } from '../../services/log-service';
import { messages } from './messages';

const RateShopperAutomationRuleContext = createContext();

export const useRateShopperAutomationRule = () => {
  const context = useContext(RateShopperAutomationRuleContext);
  if (!context) {
    throw new Error('useRateShopperAutomationRule must be used within RateShopperAutomationRuleProvider');
  }
  return context;
};

/**
 * Finds the automation rule that contains a Rate Shopper service collection action
 * @param {Array} rules - Array of automation rules
 * @returns {object|null} The rate shopper rule or null if not found
 */
const findRateShopperRule = (rules) => {
  return rules.find((rule) => {
    if (!rule.active) return false;

    const serviceAction = rule.orderRuleActions?.find(
      (action) => action.actionTypeId === ACTION_TYPE_SET_SERVICE_COLLECTION && !action.isDeleted,
    );

    return !!serviceAction;
  });
};

/**
 * Gets the current rate shopper selection from a rule
 * @param {object|null} rule - The automation rule
 * @returns {string|null} The current service collection GUID or null
 */
const getCurrentSelection = (rule) => {
  if (!rule) return null;

  const serviceAction = rule.orderRuleActions?.find(
    (action) => action.actionTypeId === ACTION_TYPE_SET_SERVICE_COLLECTION && !action.isDeleted,
  );

  return serviceAction?.parameter?.split(',')[0] || null;
};

const informationalTimeoutInMilliseconds = 3000;
const errorTimeoutInMilliseconds = 5000;

export const RateShopperAutomationRuleProvider = ({ children }) => {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(true);
  const [rateShopperRule, setRateShopperRule] = useState(null);
  const [currentSelection, setCurrentSelection] = useState(null);
  const [error, setError] = useState(null);

  const loadRules = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const rules = await fetchAutomationRules();
      const foundRule = findRateShopperRule(rules);

      setRateShopperRule(foundRule);
      setCurrentSelection(getCurrentSelection(foundRule));
    } catch (err) {
      LogService.logError(new Error('Failed to load automation rules', { cause: err }));
      setError(messages.loadAutomationRulesError);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateRateShopperSelection = useCallback(
    async (newGuid) => {
      if (!rateShopperRule) {
        setError(messages.noRuleFoundErrorWithLink);
        throw new Error(intl.formatMessage(messages.noRuleFoundError));
      }
      setError(null);

      try {
        const updatedRule = {
          ...rateShopperRule,
          orderRuleActions: rateShopperRule.orderRuleActions.map((action) => {
            if (action.actionTypeId === ACTION_TYPE_SET_SERVICE_COLLECTION && !action.isDeleted) {
              return { ...action, parameter: newGuid };
            }
            return action;
          }),
        };

        await updateRule(updatedRule);
        setRateShopperRule(updatedRule);
        setCurrentSelection(newGuid);

        NotificationService.showNotification({
          title: intl.formatMessage(messages.updateSuccessTitle),
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: intl.formatMessage(messages.updateSuccessContent),
          duration: informationalTimeoutInMilliseconds,
        });
      } catch (err) {
        LogService.logError(new Error('Failed to update rate shopper selection', { cause: err }));
        setError(messages.updateFailedError);

        NotificationService.showNotification({
          title: intl.formatMessage(messages.updateFailedTitle),
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: intl.formatMessage(messages.updateFailedError),
          duration: errorTimeoutInMilliseconds,
        });

        throw err;
      }
    },
    [rateShopperRule, intl],
  );

  const refreshRules = useCallback(() => {
    return loadRules();
  }, [loadRules]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const contextValue = {
    isLoading,
    rateShopperRule,
    currentSelection,
    error,
    updateRateShopperSelection,
    refreshRules,
    clearError,
  };

  return <RateShopperAutomationRuleContext.Provider value={contextValue}>{children}</RateShopperAutomationRuleContext.Provider>;
};
